/// <amd-module name="Agile/Common/DataStructures/CaseInsensitiveDataStructures" />
import produce from "immer";
import { IStringMap, ICustomSet } from "./SetContracts";
import { isNullOrUndefined } from "Agile/Common/Utils/NullCheck";

interface IMapData<TVal> {
    map: { [key: string]: TVal };
    keys: string[];
}

/**
 * A map where the key is case insensitive
 */
export class CaseInsensitiveMap<TVal> implements IStringMap<TVal> {
    private _data: IMapData<TVal> = {
        map: {},
        keys: []
    };

    public getKeys = () => [...this._data.keys];
    public has = (key: string): boolean => {
        if (isNullOrUndefined(key)) {
            return false;
        }
        return this._data.map[key.toLocaleLowerCase()] !== undefined;
    };
    public get = (key: string): TVal | undefined => {
        if (isNullOrUndefined(key)) {
            return undefined;
        }
        return this._data.map[key.toLocaleLowerCase()];
    };
    public set = (key: string, val: TVal): IStringMap<TVal> => {
        if (isNullOrUndefined(key)) {
            return this;
        }

        if (val === undefined) {
            return this.del(key);
        }

        return this._mutate(draft => {
            if (draft.map[key.toLocaleLowerCase()] === undefined) {
                draft.keys.push(key);
            }
            draft.map[key.toLocaleLowerCase()] = val;
        });
    };
    public del = (key: string): IStringMap<TVal> => {
        return this._mutate(draft => {
            let lowerCaseKey = key.toLocaleLowerCase();
            if (this.has(lowerCaseKey)) {
                delete draft.map[lowerCaseKey];
                draft.keys = draft.keys.filter(k => k !== lowerCaseKey);
            }
        });
    };

    private _mutate(callback: (draft: { map: { [key: string]: TVal }; keys: string[] }) => void) {
        const result = produce(this._data, callback);

        if (result !== this._data) {
            const r = new CaseInsensitiveMap<TVal>();
            r._data = result;
            return r;
        }

        return this;
    }
}

/**
 * Contains a set of case insensitive strings
 */
export class CaseInsensitiveStringSet implements ICustomSet<string> {
    private _map: IStringMap<string> = new CaseInsensitiveMap<string>();
    public add = (val: string) => {
        if (val === null || val === undefined) {
            return this;
        }
        const newMap = this._map.set(val, val);
        if (newMap !== this._map) {
            const obj = new CaseInsensitiveStringSet();
            obj._map = newMap;
            return obj;
        }
        return this;
    };
    public values = () => this._map.getKeys();
    public del = (val: string) => {
        const newMap = this._map.del(val);
        if (newMap !== this._map) {
            const obj = new CaseInsensitiveStringSet();
            obj._map = newMap;
            return obj;
        }
        return this;
    };
    public has = (val: string) => {
        return this._map.has(val);
    };
    public length = () => this._map.getKeys().length;
}
